import { Form } from 'antd';
import type { FormInstance } from 'antd';
import type { CargoItemFormValue, CargoTableRow } from './sharedTransportFormRows';
import type { SharedTransportFormValues } from './sharedTransportFormUtils';
import {
  kindCarriesSupplierOrders,
  type TransportKind,
} from '../modules/transports/transportDirection';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';

export const TRANSPORT_ORDER_SELECTION_ANCHOR_ID = 'transport-order-selection';
export const TRANSPORT_KIND_PICKER_ANCHOR_ID = 'transport-kind-picker';

export function buildTransportAddressAnchorId(variant: 'loading' | 'delivery'): string {
  return `transport-${variant}Address`;
}

export function buildTransportGoodsValueAnchorId(cargoKey: string): string {
  const selectorSafeKey = Array.from(cargoKey, (character) =>
    /[a-zA-Z0-9-]/.test(character)
      ? character
      : `_${character.codePointAt(0)?.toString(16)}_`
  ).join('');
  return `transport-goods-value-${selectorSafeKey}`;
}

export type TransportRequiredFieldKind =
  | 'kind'
  | 'supplierOrder'
  | 'order'
  | 'loadingDate'
  | 'loadingAddress'
  | 'estimatedDelivery'
  | 'deliveryAddress'
  | 'transportCompany'
  | 'goodsValue'
  | 'orderLoadingPoint'
  | 'orderDeliveryPoint';

export interface MissingTransportRequiredField {
  id: string;
  kind: TransportRequiredFieldKind;
  namePath?: Array<string | number>;
  productName?: string;
  orderNumber?: string;
}

interface TransportRequiredFieldValues {
  loadingDate?: unknown;
  loadingAddresses?: unknown;
  estimatedDelivery?: unknown;
  deliveryAddresses?: unknown;
  transportCompany?: unknown;
  cargoItemsByKey?: Record<string, CargoItemFormValue>;
  orderRoutePointsByOrder?: Record<string, { loadingAddress?: unknown; deliveryAddress?: unknown } | undefined>;
}

interface GetMissingTransportRequiredFieldsInput {
  values: TransportRequiredFieldValues;
  selectedOrderIds: string[];
  /**
   * Etap 2 (transport przywozowy): supplier orders (ZD) linked inbound. The
   * "orders" requirement is satisfied by EITHER side, so an inbound-only
   * transport is complete without a customer order. Defaults to none, which
   * reproduces the pre-Etap-2 rule for every existing caller.
   */
  selectedSupplierOrderIds?: string[];
  cargoRows: CargoTableRow[];
  canEditTransportCosts: boolean;
  /**
   * Decision 2026-10-04: rodzaj auta. `null` = not chosen yet (only the
   * kind is missing); a kind asks for its own orders — a factory truck for a
   * ZD (otherwise its freight would reach nobody), „Magazyn → klient” for a
   * ZK. `undefined` keeps the kind-less rule (either order).
   */
  transportKind?: TransportKind | null;
  /**
   * A saved factory truck that had no ZD (WP14) keeps saving with a warning:
   * the save bar does not list the ZD as missing (same as the form Alert).
   */
  supplierOrderOptional?: boolean;
  /**
   * Customer orders listed in „Trasa każdego zamówienia”: each needs a point
   * from the truck's own lists (the section's validator), so a save that the
   * validator refuses always names what is missing (review 2026-10-06).
   */
  routeOrders?: Array<{ id: string; orderNumber: string }>;
}

function hasNonBlankText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNonBlankAddress(addresses: unknown): boolean {
  return Array.isArray(addresses) && addresses.some(hasNonBlankText);
}

function hasGoodsValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  return Number.isFinite(Number(value));
}

/**
 * Single completion contract for the persistent transport-form checklist.
 *
 * Existing antd rules still own inline validation (including ranges and
 * formatting). This helper owns only the operator-facing answer to “what data
 * is still missing?” so the sticky checklist, its live updates, and tests
 * cannot drift into separate hand-written required-field lists.
 */
export function getMissingTransportRequiredFields({
  values,
  selectedOrderIds,
  selectedSupplierOrderIds = [],
  cargoRows,
  canEditTransportCosts,
  transportKind,
  supplierOrderOptional = false,
  routeOrders = [],
}: GetMissingTransportRequiredFieldsInput): MissingTransportRequiredField[] {
  const missing: MissingTransportRequiredField[] = [];

  if (transportKind === null) {
    return [{ id: 'kind', kind: 'kind' }];
  }
  if (transportKind === undefined) {
    if (selectedOrderIds.length === 0 && selectedSupplierOrderIds.length === 0) {
      missing.push({ id: 'order', kind: 'order' });
    }
  } else if (kindCarriesSupplierOrders(transportKind)) {
    if (selectedSupplierOrderIds.length === 0 && !supplierOrderOptional) {
      missing.push({ id: 'supplierOrder', kind: 'supplierOrder' });
    }
  } else if (selectedOrderIds.length === 0) {
    missing.push({ id: 'order', kind: 'order' });
  }
  if (!values.loadingDate) {
    missing.push({ id: 'loadingDate', kind: 'loadingDate', namePath: ['loadingDate'] });
  }
  if (!hasNonBlankAddress(values.loadingAddresses)) {
    missing.push({ id: 'loadingAddress', kind: 'loadingAddress', namePath: ['loadingAddresses'] });
  }
  if (!hasNonBlankAddress(values.deliveryAddresses)) {
    missing.push({
      id: 'deliveryAddress',
      kind: 'deliveryAddress',
      namePath: ['deliveryAddresses'],
    });
  }
  if (!hasNonBlankText(values.transportCompany)) {
    missing.push({
      id: 'transportCompany',
      kind: 'transportCompany',
      namePath: ['transportCompany'],
    });
  }

  const routeLists = {
    loadingAddress: normalizeTransportAddressList(values.loadingAddresses),
    deliveryAddress: normalizeTransportAddressList(values.deliveryAddresses),
  };
  routeOrders.forEach((order) => {
    (['loadingAddress', 'deliveryAddress'] as const).forEach((field) => {
      const addresses = routeLists[field];
      const value = values.orderRoutePointsByOrder?.[order.id]?.[field];
      if (addresses.length === 0 || (typeof value === 'string' && addresses.includes(value))) return;
      missing.push({
        id: `${field}:${order.id}`,
        kind: field === 'loadingAddress' ? 'orderLoadingPoint' : 'orderDeliveryPoint',
        namePath: ['orderRoutePointsByOrder', order.id, field],
        orderNumber: order.orderNumber,
      });
    });
  });

  if (canEditTransportCosts) {
    const cargoItemsByKey = values.cargoItemsByKey || {};
    cargoRows
      .filter((row) => Number(row.quantity || 0) > 0)
      .forEach((row) => {
        if (!hasGoodsValue(cargoItemsByKey[row.key]?.goodsValueTotal)) {
          missing.push({
            id: `goodsValue:${row.key}`,
            kind: 'goodsValue',
            namePath: ['cargoItemsByKey', row.key, 'goodsValueTotal'],
            productName: row.productName,
            orderNumber: row.orderNumber,
          });
        }
      });
  }

  return missing;
}

interface UseTransportRequiredFieldsInput {
  form: FormInstance<SharedTransportFormValues>;
  selectedOrderIds: string[];
  selectedSupplierOrderIds?: string[];
  cargoRows: CargoTableRow[];
  canEditTransportCosts: boolean;
  transportKind?: TransportKind | null;
  supplierOrderOptional?: boolean;
  routeOrders?: Array<{ id: string; orderNumber: string }>;
}

/** Live bridge from antd Form state to the single completion contract above. */
export function useTransportRequiredFields({
  form,
  selectedOrderIds,
  selectedSupplierOrderIds,
  cargoRows,
  canEditTransportCosts,
  transportKind,
  supplierOrderOptional,
  routeOrders,
}: UseTransportRequiredFieldsInput): MissingTransportRequiredField[] {
  const loadingDate = Form.useWatch('loadingDate', form);
  const loadingAddresses = Form.useWatch('loadingAddresses', form);
  const estimatedDelivery = Form.useWatch('estimatedDelivery', form);
  const deliveryAddresses = Form.useWatch('deliveryAddresses', form);
  const transportCompany = Form.useWatch('transportCompany', form);
  const cargoItemsByKey = Form.useWatch('cargoItemsByKey', form) as
    | Record<string, CargoItemFormValue>
    | undefined;
  const orderRoutePointsByOrder = Form.useWatch('orderRoutePointsByOrder', form) as
    TransportRequiredFieldValues['orderRoutePointsByOrder'];

  return getMissingTransportRequiredFields({
    values: {
      loadingDate,
      loadingAddresses,
      estimatedDelivery,
      deliveryAddresses,
      transportCompany,
      cargoItemsByKey,
      orderRoutePointsByOrder,
    },
    selectedOrderIds,
    selectedSupplierOrderIds,
    cargoRows,
    canEditTransportCosts,
    transportKind,
    supplierOrderOptional,
    routeOrders,
  });
}

export type TransportRequiredFieldSection = 'carrier' | 'route' | 'orders';

// Which collapsible form section a required field lives in, so the checklist
// link can expand it before focusing.
export function sectionForTransportRequiredField(
  field: Pick<MissingTransportRequiredField, 'kind'>,
): TransportRequiredFieldSection {
  switch (field.kind) {
    case 'transportCompany':
      return 'carrier';
    case 'kind':
    case 'order':
    case 'supplierOrder':
    case 'goodsValue':
      return 'orders';
    default:
      return 'route';
  }
}

export function focusTransportRequiredField(
  form: FormInstance<SharedTransportFormValues>,
  field: MissingTransportRequiredField,
  root: Document = document,
): void {
  if (field.kind === 'kind') {
    const picker = root.getElementById(TRANSPORT_KIND_PICKER_ANCHOR_ID);
    picker?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    picker?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
    return;
  }

  if (field.kind === 'order' || field.kind === 'supplierOrder') {
    const orderSelection = root.getElementById(TRANSPORT_ORDER_SELECTION_ANCHOR_ID);
    orderSelection?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const rowCheckbox = orderSelection?.querySelector<HTMLInputElement>(
      '.ant-table-tbody input[type="checkbox"], tbody input[type="checkbox"]',
    );
    const fallbackCheckbox = orderSelection?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    // The pickers live in a modal; when it is closed, focus the button that
    // opens it so the operator's next keypress lands on the right control.
    const openPickerButton = orderSelection?.querySelector<HTMLButtonElement>('[data-transport-order-picker-open]');
    (rowCheckbox || fallbackCheckbox || openPickerButton)?.focus({ preventScroll: true });
    return;
  }

  if (field.kind === 'loadingAddress' || field.kind === 'deliveryAddress') {
    const variant = field.kind === 'loadingAddress' ? 'loading' : 'delivery';
    const addressSection = root.getElementById(buildTransportAddressAnchorId(variant));
    addressSection?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    addressSection?.querySelector<HTMLElement>('textarea')?.focus({ preventScroll: true });
    return;
  }

  if (field.kind === 'goodsValue') {
    const cargoKey = String(field.namePath?.[1] || '');
    const goodsValue = root.getElementById(buildTransportGoodsValueAnchorId(cargoKey));
    goodsValue?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    goodsValue?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    return;
  }

  if (field.namePath) {
    form.scrollToField(field.namePath, {
      behavior: 'smooth',
      block: 'center',
      focus: true,
    });
  }
}
